import React, { useState, useEffect, useRef } from "react";
import axiosInstance, { fetchCsrfToken } from "../utils/axiosInstance";
import { ApiUrl, ApiSendStatus } from "../data-types/api-client/api-url";
import { Mail } from "../data-types/api-client/api-contacts";
import ReCAPTCHA from "react-google-recaptcha";
import Loading from "../components/Loading";

import MessageModal from "./MessageModal";

type ResponseStatus = "success" | "error" | null;

const ContactForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [formData, setFormData] = useState<Mail>({
    name: "",
    email: "",
    phone: "",
    text: "",
    recaptchaToken: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [isFormVisible, setIsFormVisible] = useState(true);

  const [modalTitle, setModalTitle] = useState<string>("");
  const [responseMessage, setResponseMessage] = useState<string>("");
  const [responseStatus, setResponseStatus] = useState<ResponseStatus>(null);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const setModal = (status: string) => {
    switch (status) {
      case ApiSendStatus.OK:
        setResponseStatus("success");
        setModalTitle("Dakujeme za Váš email!");
        setResponseMessage("Email bol úspešne odoslaný.");
        break;
      case ApiSendStatus.ERROR:
        setResponseStatus("error");
        setModalTitle("Došlo k chybe!");
        setResponseMessage("Chyba pri odosielaní. Skúste znova.");
        break;
      case ApiSendStatus.ERROR_TOO_MANY_ATTEMPTS:
        setResponseStatus("error");
        setModalTitle("Došlo k chybe!");
        setResponseMessage("Príliš veľa pokusov. Skúste neskôr.");
        break;
      default:
        setResponseStatus("error");
        setModalTitle("Došlo k chybe!");
        setResponseMessage("Chyba pri odosielaní. Skúste znova.");
    }
  };

  const resetModal = () => {
    setModalTitle("");
    setResponseMessage("");
    setResponseStatus(null);
  };
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Prosím, overte reCAPTCHA.");
      return;
    }

    resetModal();
    setIsModalOpen(false);

    const payload = {
      ...formData,
      recaptchaToken: captchaToken,
    };

    setLoading(true);

    axiosInstance
      .post<{ status: string }>(`${ApiUrl.ContactsApiUrl}/send`, payload)
      .then((res) => {
        const response = res.data.status;
        setModal(response);
        setIsModalOpen(true);

        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      })

      .catch((err) => {
        console.error(err);
        setModal(err.response.data.status);
        setIsModalOpen(true);

        recaptchaRef.current?.reset();
        setCaptchaToken(null);
      })
      .finally(() => {
        setLoading(false);
      });

    setFormData({
      name: "",
      email: "",
      phone: "",
      text: "",
      recaptchaToken: "",
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // setIsFormVisible(false);
  };

  useEffect(() => {
    fetchCsrfToken().catch((err) => console.error("CSRF fetch failed", err));
  }, []);

  return (
    <div className="flex flex-col items-center xl:px-20 px-7 rounded-lg bg-bg-light w-full h-full py-6">
      <h2 className="text-xl font-semibold text-center mb-8">
        Napíšte nám správu
      </h2>
      <form onSubmit={handleSubmit} className="w-full">
        <div className="mb-4">
          <label
            htmlFor="name"
            className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
          >
            <span>Meno a Priezvisko / Názov firmy</span>
            <span className="text-custom-orange">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full p-2 bg-custom-white border border-custom-blue/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Vaše Meno a Priezvisko / Názov firmy"
          />
        </div>

        <div className="flex flex-col w-full justify-between items-center gap-4">
          {" "}
          <div className="w-full">
            <label
              htmlFor="email"
              className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
            >
              <span>Email</span>
              <span className="text-custom-orange">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full p-2 bg-custom-white border border-custom-blue/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Váš email"
            />
          </div>
          <div className="mb-4 w-full">
            <label
              htmlFor="phone"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Telefónne číslo
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-2 bg-custom-white border border-custom-blue/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Vaše telefónne číslo"
            />
          </div>
        </div>

        <div className="mb-4">
          <label
            htmlFor="text"
            className="flex items-center space-x-1 text-gray-700 mb-2 text-sm font-medium"
          >
            <span>Správa</span>
            <span className="text-custom-orange">*</span>
          </label>
          <textarea
            id="text"
            name="text"
            maxLength={1024}
            value={formData.text}
            onChange={handleChange}
            required
            rows={4}
            className="w-full h-64 p-2 text-gray-700 bg-custom-white border border-custom-blue/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="S čím Vám môžme pomôcť?"
          />
        </div>

        <div className="w-full flex items-center justify-center">
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ""}
            onChange={handleCaptchaChange}
            ref={recaptchaRef}
            className="mb-5"
          />
        </div>
        <div className="w-full flex items-center justify-center">
          {" "}
          <button
            type="submit"
            className="flex w-40 px-2 py-3 border border-custom-blue/70 bg-custom-blue text-custom-white rounded-lg justify-self-center items-center justify-center font-semibold text-gray-700 hover:bg-custom-red hover:border-custom-red hover:text-custom-white transition duration-200"
          >
            Odoslať
          </button>
        </div>
        {loading && <Loading />}
      </form>

      <MessageModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={modalTitle}
        message={responseMessage}
        success={responseStatus}
      />
    </div>
  );
};

export default ContactForm;
